import { yupResolver } from "@hookform/resolvers/yup";
import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useForm } from "react-hook-form";
import useSchema from "../../../hooks/useSchema";
import {
  DEFAULT_PHONE_COUNTRY,
  PHONE_COUNTRIES,
  composePhone,
  detectPhoneCountry,
  sanitizeEmail,
  toNsn,
} from "../../../utils/phoneHelper";
import { useTranslation } from "react-i18next";
import { useKeyboard } from "../../../context/KeyboardContext";
import { useDispatch, useSelector } from "react-redux";
import {
  setKeyboardConfiguration,
  setLoyaltyData,
  setShowKeyboard,
  addToOrder,
} from "../../../redux/slices";

const RegisterMember = ({ closeFunc }) => {
  const { t } = useTranslation(["OrderPayment", "Common"]);
  const { memberPlans } = useSelector((state) => state.loyalty);
  const { setSetSharedInput } = useKeyboard();
  const { buildRegisterMemberSchema } = useSchema();
  const [countryCode, setCountryCode] = useState(DEFAULT_PHONE_COUNTRY);
  // Holds the national number alone — the selector supplies the dial code.
  const [nationalNumber, setNationalNumber] = useState("");
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  // Plan is managed directly by react-hook-form; no separate local state needed.
  const dispatch = useDispatch();
  const resetFunc = () => {
    setValue("phoneNumber", "");
    setValue("email", "");
    setValue("firstName", "");
    setValue("lastName", "");
    setValue("plan", null);
    setNationalNumber("");
    setEmail("");
    setFirstName("");
    setLastName("");
    closeFunc();
  };
  const schema = useMemo(
    () => buildRegisterMemberSchema(countryCode),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [countryCode]
  );

  const {
    register,
    setValue,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      phoneNumber: "",
      email: "",
      firstName: "",
      lastName: "",
      plan: null,
    },
    mode: "onChange",
  });
  // The field holds the national number; RHF and the API get the composed form.
  const setPhoneValue = (nsn, country) => {
    setNationalNumber(nsn);
    setValue("phoneNumber", composePhone(nsn, country), {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const onPhoneChange = (value) => {
    // A pasted full number carries its own dial code; follow it.
    const detected = detectPhoneCountry(value);
    const country = detected || countryCode;
    if (detected && detected !== countryCode) setCountryCode(detected);
    setPhoneValue(toNsn(value, country), country);
  };

  const onCountryChange = (nextCode) => {
    setCountryCode(nextCode);
    // Re-cap against the new country, which may be shorter.
    setPhoneValue(toNsn(nationalNumber, nextCode), nextCode);
  };

  // Inputs are now directly registered and update RHF via onChange; no effect mirroring needed.
  const orderItems = useSelector((state) => state.orders.orderItems);

  const onSubmit = (formData) => {
    const selectedPlan = formData.plan;

    dispatch(
      setLoyaltyData({
        phoneNumber: formData.phoneNumber || null,
        email: formData.email || null,
        firstName: formData.firstName || null,
        lastName: formData.lastName || null,
        plan: selectedPlan || null,

      })
    );

    // If a plan was selected, add it to the cart exactly once and make it non-modifiable
    if (selectedPlan) {
      const alreadyAdded = orderItems.some(
        (i) => i.id === selectedPlan.id && i.type === "loyaltyPlan"
      );
      if (!alreadyAdded) {
        dispatch(
          addToOrder({
            memberShipPlanid: selectedPlan.id, 
            name: selectedPlan.name,
            price: selectedPlan.price || selectedPlan.amount || 0, // fallback defensively
            orderQuantity: 1,
            canModify: false, // prevents +/- quantity changes in cart UI
            isLoyaltyPlan: true, // custom flag so we can identify if order item is a loyalty plan
          })
        );
      }
    }


    closeFunc();
  };
  const watchedPlan = watch("plan");

  return (
    <section className="flex flex-col justify-center items-center h-full p-4">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-2 gap-x-5">
          <div className="mt-2 deleteModal flex justify-center ">
            <div>
              <label className="text-center block ">
                {t("OrderPayment:paymentchannel_modal.label.phone")}
              </label>

              <div className="flex">
                <select
                  value={countryCode}
                  onChange={(e) => onCountryChange(e.target.value)}
                  className="h-[50px] border border-secondary border-r-0 rounded-l py-2 pl-2 pr-1 bg-white"
                  aria-label={t("Common:label.country")}
                >
                  {Object.values(PHONE_COUNTRIES).map((country) => (
                    <option key={country.code} value={country.code}>
                      +{country.dialCode}
                    </option>
                  ))}
                </select>
                <input
                  type="tel"
                  inputMode="numeric"
                  maxLength={PHONE_COUNTRIES[countryCode]?.nsnLength}
                  placeholder={
                    PHONE_COUNTRIES[countryCode]?.example ||
                    t("Common:keyboard.placeholder.customer.phone")
                  }
                  className=" w-[100%]  h-[50px] border border-secondary rounded-r py-2 px-3"
                  value={nationalNumber}
                  onChange={(e) => onPhoneChange(e.target.value)}
                  onClick={() => {
                    dispatch(setShowKeyboard(true));
                    dispatch(
                      setKeyboardConfiguration({
                        type: "numeric",
                        sharedInput: nationalNumber,
                        placeHolder: t(
                          "Common:keyboard.placeholder.customer.phone"
                        ),
                      })
                    );
                    setSetSharedInput(() => onPhoneChange);
                  }}
                />
              </div>
              {errors?.phoneNumber?.message && (
                <p className="text-red-500 text-sm">
                  {errors.phoneNumber.message}
                </p>
              )}
            </div>
          </div>
          <div className="mt-2 deleteModal flex justify-center">
            <div>
              <label className="text-center block">
                {t("OrderPayment:paymentchannel_modal.label.email")}
              </label>

              <input
                type="email"
                placeholder={t("Common:keyboard.placeholder.customer.email")}
                className=" w-[100%]  h-[50px] border border-secondary rounded py-2 px-3"
                {...register("email")}
                value={email}
                maxLength={254}
                onChange={(e) => {
                  const v = sanitizeEmail(e.target.value);
                  setEmail(v);
                  setValue("email", v, {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                }}
                onClick={() => {
                  dispatch(setShowKeyboard(true));
                  dispatch(
                    setKeyboardConfiguration({
                      type: "alphaNumeric",
                      sharedInput: email,
                      placeHolder: t(
                        "Common:keyboard.placeholder.customer.email"
                      ),
                    })
                  );
                  setSetSharedInput(() => (val) => {
                    const cleaned = sanitizeEmail(val);
                    setEmail(cleaned);
                    setValue("email", cleaned, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                  });
                }}
              />
              {errors?.email?.message && (
                <p className="text-red-500 text-sm">{errors?.email?.message}</p>
              )}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-x-5">
          <div className="mt-2 deleteModal flex justify-center">
            <div>
              <label className="text-center block">
                {t("Common:label.name.first")}
              </label>

              <input
                type="text"
                placeholder={t(
                  "Common:keyboard.placeholder.customer.firstName"
                )}
                className=" w-[100%]  h-[50px] border border-secondary rounded py-2 px-3"
                {...register("firstName")}
                value={firstName}
                onChange={(e) => {
                  const v = e.target.value;
                  setFirstName(v);
                  setValue("firstName", v, {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                }}
                onClick={() => {
                  dispatch(setShowKeyboard(true));
                  dispatch(
                    setKeyboardConfiguration({
                      type: "alphaNumeric",
                      sharedInput: firstName,
                      placeHolder: t(
                        "Common:keyboard.placeholder.customer.firstName"
                      ),
                    })
                  );
                  setSetSharedInput(() => (val) => {
                    setFirstName(val);
                    setValue("firstName", val, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                  });
                }}
              />
              {errors?.firstName?.message && (
                <p className="text-red-500 text-sm">
                  {errors?.firstName?.message}
                </p>
              )}
            </div>
          </div>
          <div className="mt-2 deleteModal flex justify-center">
            <div>
              <label className="text-center block">
                {t("Common:label.name.last")}
              </label>

              <input
                type="text"
                placeholder={t("Common:keyboard.placeholder.customer.lastName")}
                className=" w-[100%]  h-[50px] border border-secondary rounded py-2 px-3"
                {...register("lastName")}
                value={lastName}
                onChange={(e) => {
                  const v = e.target.value;
                  setLastName(v);
                  setValue("lastName", v, {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                }}
                onClick={() => {
                  dispatch(setShowKeyboard(true));
                  dispatch(
                    setKeyboardConfiguration({
                      type: "alphaNumeric",
                      sharedInput: lastName,
                      placeHolder: t(
                        "Common:keyboard.placeholder.customer.lastName"
                      ),
                    })
                  );
                  setSetSharedInput(() => (val) => {
                    setLastName(val);
                    setValue("lastName", val, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                  });
                }}
              />
              {errors?.lastName?.message && (
                <p className="text-red-500 text-sm">
                  {errors?.lastName?.message}
                </p>
              )}
            </div>
          </div>
          <div className="mt-2 deleteModal">
            <div>
              <label className="text-center block">{"Select Plan"}</label>
              <select
                value={watchedPlan?.id || ""}
                onChange={(e) => {
                  const selectedId = e.target.value;
                  const planObj =
                    memberPlans?.find(
                      (p) => String(p.id) === String(selectedId)
                    ) || null;
                  setValue("plan", planObj, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                }}
                className="w-[100%] px-3 h-[50px] border border-secondary rounded py-2"
              >
                <option value="" className="text-gray-400" disabled>
                  {"Select Plan"}
                </option>
                {memberPlans?.map((plan) => (
                  <option key={plan.id} value={plan.id}>
                    {plan?.name}
                  </option>
                ))}
              </select>
              {errors?.plan?.message && (
                <p className="text-red-500 text-sm">{errors?.plan?.message}</p>
              )}
            </div>
          </div>
        </div>

        <div className="mt-5 flex justify-center">
          <button
            type="button"
            onClick={() => {
              resetFunc();
            }}
            className=" inline-flex justify-center rounded-md border border-transparent bg-red-700 px-4 py-2 text-sm font-medium text-white mr-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
          >
            {t("Common:button.cancel")}
          </button>
          <button
            type="submit"
            className=" ml-2 inline-flex items-center justify-center rounded-md border border-transparent bg-blue-100 px-4 py-2 text-sm font-medium text-blue-900 hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
          >
            <span>{t("Common:button.submit")}</span>
            {/* <Spinner
              className="!w-3 !h-3 mx-1 text-white fill-blue-800"
              status={customerDataStatus}
            /> */}
          </button>
        </div>
      </form>
    </section>
  );
};

export default RegisterMember;

RegisterMember.propTypes = {
  closeFunc: PropTypes.func.isRequired,
};
